import React, { useState, useEffect } from 'react';
import {
  Container,
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  Chip,
  Alert,
  CircularProgress,
  Stack,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Tooltip
} from '@mui/material';
import {
  Add as AddIcon,
  Home as HomeIcon,
  Groups as GroupsIcon,
  ContentCopy as CopyIcon,
  PersonAdd as PersonAddIcon,
  Map as MapIcon,
  Send as SendIcon,
  ArrowForward as ArrowForwardIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import api from '../config/api';
import { documentOutlineButtonSx } from '../constants/documentTheme';
import { getTripPrimaryLocation } from '../utils/tripUtils';
import LocationAutocomplete from '../components/LocationAutocomplete';
import PartnerPlanWall from '../components/PartnerPlanWall';
import usePartnerEntitlements from '../hooks/usePartnerEntitlements';
import { canCreatePartnerGroupTrips, isPartnerPlanLimitError } from '../utils/partnerPlan';

const PartnerMasterTrips = () => {
  const navigate = useNavigate();
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [planWallOpen, setPlanWallOpen] = useState(false);
  const [planEntitlements, setPlanEntitlements] = useState(null);
  const { entitlements } = usePartnerEntitlements();
  const groupTripsLocked = canCreatePartnerGroupTrips(entitlements) === false;
  const [newTrip, setNewTrip] = useState({
    title: '',
    description: '',
    startDate: '',
    endDate: '',
    tripType: 'vacation',
    locations: [{ name: '' }]
  });

  useEffect(() => {
    fetchMasterTrips();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchMasterTrips = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('partnerToken');
      
      if (!token) {
        navigate('/partner/login');
        return;
      }

      const response = await api.get('/partners/master-trips', {
        headers: { Authorization: `Bearer ${token}` }
      });

      setTrips(response.data.trips);
    } catch (err) {
      console.error('Error fetching master trips:', err);
      if (err.response?.status === 401) {
        localStorage.removeItem('partnerToken');
        navigate('/partner/login');
      } else {
        setError('Failed to load group trips');
      }
    } finally {
      setLoading(false);
    }
  };

  const openCreateDialog = () => {
    if (groupTripsLocked) {
      setPlanEntitlements(entitlements);
      setPlanWallOpen(true);
      return;
    }
    setCreateDialogOpen(true);
  };

  const handleCreateTrip = async () => {
    try {
      setCreating(true);
      const token = localStorage.getItem('partnerToken');
      
      const response = await api.post('/partners/master-trips', {
        title: newTrip.title,
        description: newTrip.description,
        startDate: newTrip.startDate,
        endDate: newTrip.endDate,
        tripType: newTrip.tripType,
        locations: newTrip.locations.filter(l => l.name.trim())
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setSuccess(`Group trip created! Trip code: ${response.data.tripCode}`);
      setCreateDialogOpen(false);
      setNewTrip({ title: '', description: '', startDate: '', endDate: '', tripType: 'vacation', locations: [{ name: '' }] });
      fetchMasterTrips();
    } catch (err) {
      console.error('Error creating master trip:', err);
      if (isPartnerPlanLimitError(err)) {
        setPlanEntitlements(err.response.data.entitlements || null);
        setPlanWallOpen(true);
        setCreateDialogOpen(false);
      }
      setError(err.response?.data?.error || 'Failed to create group trip');
    } finally {
      setCreating(false);
    }
  };

  const copyTripCode = (code) => {
    navigator.clipboard.writeText(code);
    setSuccess('Trip code copied to clipboard!');
    setTimeout(() => setSuccess(''), 3000);
  };

  const getTripStatus = (trip) => {
    const now = new Date();
    const start = new Date(trip.start_date);
    const end = new Date(trip.end_date);

    if (now < start) return { label: 'Upcoming', color: 'primary' };
    if (now > end) return { label: 'Completed', color: 'default' };
    return { label: 'Active', color: 'success' };
  };

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ py: 8 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 8 }}>
      {/* Header */}
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h3" gutterBottom sx={{ fontWeight: 600 }}>
            Group Trips
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage master itineraries with individual traveler logistics
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<HomeIcon />}
            onClick={() => navigate('/partner/dashboard')}
            sx={{
              borderColor: '#8B1538',
              color: '#8B1538',
              px: 3,
              py: 1.5,
              fontWeight: 600,
              '&:hover': { borderColor: '#7A1230', backgroundColor: 'rgba(139, 21, 56, 0.06)' }
            }}
          >
            Dashboard
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={openCreateDialog}
            sx={{
              background: 'linear-gradient(135deg, #8B1538 0%, #6B1028 100%)',
              color: 'white',
              px: 3,
              py: 1.5,
              fontWeight: 600,
              '&:hover': { background: 'linear-gradient(135deg, #7A1230 0%, #5A0E20 100%)' }
            }}
          >
            Create Group Trip
          </Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>{success}</Alert>}

      {/* How It Works Steps */}
      <Paper sx={{ 
        p: { xs: 2, md: 3 }, 
        mb: 4, 
        borderRadius: 3, 
        bgcolor: '#fafafa', 
        border: '1px solid rgba(0,0,0,0.06)',
        boxShadow: 'none'
      }}>
        <Typography variant="overline" sx={{ 
          fontWeight: 700, 
          color: 'text.secondary', 
          letterSpacing: 1, 
          fontSize: '0.7rem',
          mb: 2 
        }}>
          How it works
        </Typography>
        <Grid container spacing={{ xs: 1.5, md: 2 }} alignItems="stretch">
          {[
            { 
              step: 1, 
              title: 'Create Group Trip', 
              desc: 'Set destination, dates & trip type',
              icon: <AddIcon sx={{ fontSize: 22 }} />,
              color: '#8B1538',
            },
            { 
              step: 2, 
              title: 'Build Shared Itinerary', 
              desc: 'Add group activities & schedule',
              icon: <MapIcon sx={{ fontSize: 22 }} />,
              color: '#1976d2',
            },
            { 
              step: 3, 
              title: 'Add Travelers', 
              desc: 'Each gets their own personal trip',
              icon: <PersonAddIcon sx={{ fontSize: 22 }} />,
              color: '#2e7d32',
            },
            { 
              step: 4, 
              title: 'Manage & Communicate', 
              desc: 'Add flights, hotels & broadcast updates',
              icon: <SendIcon sx={{ fontSize: 22 }} />,
              color: '#ed6c02',
            },
          ].map((item, index) => (
            <Grid item xs={6} md={3} key={item.step}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  p: { xs: 1.5, md: 2 },
                  borderRadius: 3,
                  bgcolor: 'white',
                  border: '1px solid rgba(0,0,0,0.06)',
                  height: '100%',
                  position: 'relative',
                }}
              >
                {/* Step number + icon */}
                <Box sx={{ 
                  minWidth: 40,
                  height: 40,
                  borderRadius: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: `${item.color}12`,
                  color: item.color,
                  position: 'relative',
                }}>
                  {item.icon}
                  <Box sx={{
                    position: 'absolute',
                    top: -6,
                    left: -6,
                    width: 18,
                    height: 18,
                    borderRadius: '50%',
                    bgcolor: item.color,
                    color: 'white',
                    fontSize: '0.65rem',
                    fontWeight: 800,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: '2px solid white',
                  }}>
                    {item.step}
                  </Box>
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body2" sx={{ 
                    fontWeight: 700, 
                    fontSize: { xs: '0.8rem', md: '0.85rem' },
                    color: 'text.primary',
                    lineHeight: 1.3,
                  }}>
                    {item.title}
                  </Typography>
                  <Typography variant="caption" sx={{ 
                    color: 'text.secondary', 
                    fontSize: '0.7rem',
                    lineHeight: 1.3,
                    display: { xs: 'none', sm: 'block' },
                  }}>
                    {item.desc}
                  </Typography>
                </Box>
                {/* Arrow connector (desktop only, not on last) */}
                {index < 3 && (
                  <ArrowForwardIcon sx={{ 
                    display: { xs: 'none', md: 'block' },
                    position: 'absolute',
                    right: -14,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    fontSize: 16,
                    color: 'rgba(0,0,0,0.15)',
                    zIndex: 2,
                  }} />
                )}
              </Box>
            </Grid>
          ))}
        </Grid>
      </Paper>

      {/* Trips Grid */}
      {trips.length === 0 ? (
        <Card elevation={2}>
          <CardContent sx={{ py: 8, textAlign: 'center' }}>
            <GroupsIcon sx={{ fontSize: 80, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h5" gutterBottom color="text.secondary">
              No Group Trips Yet
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              Create your first group trip to manage shared itineraries with individual traveler logistics
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={openCreateDialog}
              sx={{ background: 'linear-gradient(135deg, #8B1538 0%, #6B1028 100%)' }}
            >
              Create Group Trip
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Grid container spacing={{ xs: 1.25, sm: 1.5 }}>
          {trips.map((trip) => {
            const tripStatus = getTripStatus(trip);
            const location = getTripPrimaryLocation(trip);
            
            return (
              <Grid item xs={12} md={6} key={trip.id}>
                <Card 
                  sx={{ 
                    display: 'flex',
                    border: '1px solid #e0e0e0',
                    borderRadius: 0,
                    overflow: 'hidden',
                    boxShadow: 'none',
                    cursor: 'pointer',
                    '&:hover': { borderColor: '#8B1538' }
                  }}
                  onClick={() => navigate(`/partner/master-trips/${trip.id}`)}
                >
                  <CardContent sx={{ p: 1.75, display: 'flex', flexDirection: 'column', minWidth: 0, flex: 1 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" mb={0.75}>
                      <Chip 
                        label={tripStatus.label} 
                        color={tripStatus.color}
                        size="small"
                        sx={{ fontWeight: 600 }}
                      />
                      {trip.trip_code && (
                        <Tooltip title="Click to copy trip code">
                          <Chip
                            icon={<CopyIcon sx={{ fontSize: 14 }} />}
                            label={trip.trip_code}
                            size="small"
                            variant="outlined"
                            onClick={(e) => { e.stopPropagation(); copyTripCode(trip.trip_code); }}
                            sx={{ fontFamily: 'monospace', fontWeight: 600 }}
                          />
                        </Tooltip>
                      )}
                    </Stack>

                    <Typography variant="h6" fontWeight={600} noWrap sx={{ fontSize: 18, mb: 0.35 }}>
                      {trip.title}
                    </Typography>

                    {location && location !== 'No destination set' && (
                      <Typography variant="body2" color="text.secondary" noWrap sx={{ mb: 0.25 }}>
                        {location}
                      </Typography>
                    )}

                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                      {trip.start_date ? format(new Date(trip.start_date), 'EEE, MMM d') : '?'} – {trip.end_date ? format(new Date(trip.end_date), 'EEE, MMM d, yyyy') : '?'}
                    </Typography>

                    <Typography variant="body2" color="text.secondary">
                      {trip.traveler_count || 0} travelers · {trip.itinerary_item_count || 0} activities
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* Create Group Trip Dialog */}
      <Dialog 
        open={createDialogOpen} 
        onClose={() => setCreateDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { overflow: 'visible' } }}
      >
        <DialogTitle sx={{ fontWeight: 600, borderBottom: '1px solid #eee' }}>
          Create Group Trip
        </DialogTitle>
        <DialogContent sx={{ mt: 2, overflow: 'visible' }}>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <TextField
              label="Trip Title"
              fullWidth
              required
              value={newTrip.title}
              onChange={(e) => setNewTrip(prev => ({ ...prev, title: e.target.value }))}
              placeholder="e.g., African Safari Adventure 2026"
            />
            <TextField
              label="Description"
              fullWidth
              multiline
              rows={3}
              value={newTrip.description}
              onChange={(e) => setNewTrip(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Describe the shared experience for all travelers..."
            />
            <Stack direction="row" spacing={2}>
              <TextField
                label="Start Date"
                type="date"
                fullWidth
                required
                InputLabelProps={{ shrink: true }}
                value={newTrip.startDate}
                onChange={(e) => setNewTrip(prev => ({ ...prev, startDate: e.target.value }))}
              />
              <TextField
                label="End Date"
                type="date"
                fullWidth
                required
                InputLabelProps={{ shrink: true }}
                value={newTrip.endDate}
                onChange={(e) => setNewTrip(prev => ({ ...prev, endDate: e.target.value }))}
              />
            </Stack>
            <TextField
              label="Trip Type"
              select
              fullWidth
              value={newTrip.tripType}
              onChange={(e) => setNewTrip(prev => ({ ...prev, tripType: e.target.value }))}
            >
              <MenuItem value="vacation">Vacation</MenuItem>
              <MenuItem value="adventure">Adventure</MenuItem>
              <MenuItem value="business">Business</MenuItem>
              <MenuItem value="family">Family</MenuItem>
              <MenuItem value="beach">Beach</MenuItem>
              <MenuItem value="city">City</MenuItem>
              <MenuItem value="historical">Historical</MenuItem>
              <MenuItem value="outdoors">Outdoors</MenuItem>
              <MenuItem value="luxury">Luxury</MenuItem>
            </TextField>
            <LocationAutocomplete
              label="Destination"
              value={newTrip.locations[0]?.name || ''}
              onChange={(val) => setNewTrip(prev => ({ ...prev, locations: [{ name: val }] }))}
              onPlaceSelect={(place) => setNewTrip(prev => ({ ...prev, locations: [{ name: place.address || place.name }] }))}
              placeholder="Search for a destination..."
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 3, borderTop: '1px solid #eee' }}>
          <Button onClick={() => setCreateDialogOpen(false)} sx={{ ...documentOutlineButtonSx, minWidth: 120 }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleCreateTrip}
            disabled={creating || !newTrip.title || !newTrip.startDate || !newTrip.endDate}
            sx={{ 
              background: 'linear-gradient(135deg, #8B1538 0%, #6B1028 100%)',
              '&:hover': { background: 'linear-gradient(135deg, #7A1230 0%, #5A0E20 100%)' }
            }}
          >
            {creating ? <CircularProgress size={24} /> : 'Create Group Trip'}
          </Button>
        </DialogActions>
      </Dialog>
      <PartnerPlanWall
        open={planWallOpen}
        onClose={() => setPlanWallOpen(false)}
        entitlements={planEntitlements || entitlements}
        reason="group"
      />
    </Container>
  );
};

export default PartnerMasterTrips;
